/**
 * Small pieces the company and deal screens share: money in pence, role
 * chips in the organisation's words, the owner picker and who may edit.
 */
import { useMemo } from 'react';
import { Badge, Group, Select, type SelectProps } from '@mantine/core';
import { useSegmentPeople } from '../../api/queries';
import { useCompanies, type CompanyRoleKey, type DealStatus } from '../../api/crm';
import { canDo, seesPeople, visiblePaths } from '../../layout/commands';
import { useNavUser } from '../../layout/navUser';
import { useVocab, type Vocab } from '../../vocab';

const pounds = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 });
const wholePounds = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

/** £1,200 for whole pounds, £1,200.50 otherwise. Money is stored in pence. */
export function formatPence(pence: number): string {
  return pence % 100 === 0 ? wholePounds.format(pence / 100) : pounds.format(pence / 100);
}

/** Pounds as typed (a number from the input) to whole pence, or null when blank. */
export function poundsToPence(value: number | string | null | undefined): number | null {
  if (value === '' || value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[£,\s]/g, ''));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/**
 * A company role in the app's words: data buyer, data seller, customer.
 * The keys (`data_buyer`, `data_seller`) never change.
 */
export function roleWord(v: Vocab, role: CompanyRoleKey): string {
  switch (role) {
    case 'data_buyer': return v.p.dataBuyer;
    case 'data_seller': return v.p.dataSeller;
    case 'customer': return 'Customer';
  }
}

/** The role filters, in the order the list and the menu use. */
export const ROLE_KEYS: CompanyRoleKey[] = ['data_buyer', 'data_seller', 'customer'];

/** A role from the address (`?role=`), old names included, or null. */
export function roleFromParam(raw: string | null): CompanyRoleKey | null {
  switch (raw) {
    case 'data_buyer': case 'partner': case 'buyer': return 'data_buyer';
    case 'data_seller': case 'source': case 'supplier': return 'data_seller';
    case 'customer': return 'customer';
    default: return null;
  }
}

const ROLE_COLOUR: Record<CompanyRoleKey, string> = { data_buyer: 'blue', data_seller: 'grape', customer: 'chartGreen' };

export function RoleChips({ roles, archived }: { roles: CompanyRoleKey[]; archived?: Partial<Record<CompanyRoleKey, boolean>> }) {
  const v = useVocab();
  if (roles.length === 0) return null;
  return (
    <Group gap={4} wrap="wrap">
      {roles.map((r) => (
        <Badge key={r} variant="light" color={archived?.[r] ? 'gray' : ROLE_COLOUR[r]} radius="sm" tt="none">
          {archived?.[r] ? `${roleWord(v, r)}, off` : roleWord(v, r)}
        </Badge>
      ))}
    </Group>
  );
}

export function statusColour(status: DealStatus): string {
  return status === 'won' ? 'chartGreen' : status === 'lost' ? 'red' : 'gray';
}

export const STATUS_LABEL: Record<DealStatus, string> = { open: 'Open', won: 'Won', lost: 'Lost' };

const SCOPED_ROLES = new Set(['buyer', 'supplier', 'unapproved']);
const WHOLE_ORG_ROLES = new Set(['super_admin', 'admin', 'manager', 'analyst', 'support', 'viewer']);

/**
 * Which CRM records this login sees, as the server decides it
 * (`effective_record_scope`): everything for the existing roles, their own
 * (or their team's, leading one) for a sales rep, nothing for a scoped login.
 * Only for what the screen offers; the server narrows the data itself.
 */
export function recordScope(user: { role?: string | null; lead_of_team_ids?: string[] | null } | null): 'all' | 'team' | 'own' | 'none' {
  if (!user) return 'none';
  const role = user.role ?? '';
  if (WHOLE_ORG_ROLES.has(role)) return 'all';
  if (role === 'sales') return (user.lead_of_team_ids?.length ?? 0) > 0 ? 'team' : 'own';
  return 'none';
}

/** What this person may do here, from the same rules as the rail and ⌘K. */
export function useCrmPermissions() {
  // The tabs and permissions decide; the old `features` switches no longer
  // hide the CRM.
  const user = useNavUser();
  return useMemo(() => {
    // Nobody signed in may see nothing, rather than the full menu navFor()
    // gives a null user.
    const paths = user ? visiblePaths(user) : new Set<string>();
    return {
      user,
      seesDeals: paths.has('/deals'),
      seesCompanies: paths.has('/companies'),
      editDeals: canDo(user, 'edit_deals'),
      editCompanies: canDo(user, 'edit_companies'),
      managePipelines: canDo(user, 'manage_pipelines'),
      seesTasks: paths.has('/tasks'),
      /**
       * People, anywhere: the page, the "+N more" badges, the counts toggle
       * and the command bar group. Never for a buyer, supplier, pending or
       * otherwise scoped login.
       */
      seesPeople: seesPeople(user),
      /**
       * Merge, unlink, undo and accept suggestions. The server says so per
       * person too (`can_link`).
       */
      linkPeople: seesPeople(user) && canDo(user, 'link_people') && recordScope(user) === 'all',
      /**
       * The people-linking controls: the suggestions inbox, Settings ›
       * People and each stream's linking choice.
       */
      peopleLinking: seesPeople(user) && recordScope(user) === 'all',
      /** Notes, tasks and calls on anything, and anyone's. */
      editActivities: canDo(user, 'edit_activities'),
      /**
       * Owners on people, companies and deals: the owner column, picker and
       * filter. Never for a scoped login.
       */
      owners: !!user && !SCOPED_ROLES.has(user.role ?? ''),
      /** Teams and their rotations (manage_teams). */
      manageTeams: canDo(user, 'manage_teams'),
      /**
       * Bulk Assign owner, sending people through a rotation, and giving a
       * record that someone else owns to another (assign_records).
       */
      assignRecords: canDo(user, 'assign_records'),
      /**
       * Property definitions in Settings (manage_properties). Not tied to
       * the CRM area: lead properties label what distribution reads.
       */
      manageProperties: !!user && !SCOPED_ROLES.has(user.role ?? '') && canDo(user, 'manage_properties'),
      /**
       * Properties on people, companies and deals: their sections, list
       * columns and inputs.
       */
      recordProperties: !!user && !SCOPED_ROLES.has(user.role ?? ''),
      /**
       * Timelines at all: every member except buyer, supplier and pending
       * logins. A member limited to some campaigns is refused by the server,
       * and the panel says so.
       */
      seesTimeline: !!user && !SCOPED_ROLES.has(user.role ?? ''),
      /**
       * A company's "Buys from us" and "Sells to us": whoever has the
       * Buyers or Suppliers page (its `buyers` / `suppliers` tab). Those
       * pages are now these filtered company views.
       */
      seesBuyers: paths.has('/buyers'),
      seesSuppliers: paths.has('/suppliers'),
      /** Change a buyer or supplier record, add or take away the role. */
      editBuyers: canDo(user, 'edit_buyers'),
      editSuppliers: canDo(user, 'edit_suppliers'),
      /** Statements, drafts and billing details. */
      manageBilling: canDo(user, 'manage_billing'),
    };
  }, [user]);
}

/**
 * Members who can own a record: everyone in the organisation except buyer,
 * supplier and pending logins, who never see these records.
 */
export function useOwnerOptions(enabled = true) {
  const people = useSegmentPeople(enabled).data?.people ?? [];
  return people
    .filter((p) => !['buyer', 'supplier', 'unapproved'].includes(p.role))
    .map((p) => ({ value: p.id, label: p.is_you ? `${p.name} (you)` : p.name }));
}

export function OwnerSelect(props: Omit<SelectProps, 'data'>) {
  const data = useOwnerOptions();
  return <Select searchable clearable placeholder="Nobody" nothingFoundMessage="No one by that name" data={data} {...props} />;
}

/** Pick a company by name; searches as you type. */
export function CompanySelect({
  value, valueLabel, onChange, ...rest
}: Omit<SelectProps, 'data' | 'value' | 'onChange'> & {
  value: string | null;
  /** The chosen company's name, before the list has loaded. */
  valueLabel?: string | null;
  onChange: (id: string | null, name: string | null) => void;
}) {
  const v = useVocab();
  const list = useCompanies({ page_size: 500 }).data?.companies ?? [];
  const data = list.map((c) => ({ value: c.id, label: c.name }));
  if (value && !data.some((d) => d.value === value)) data.unshift({ value, label: valueLabel ?? value });
  return (
    <Select
      searchable
      clearable
      placeholder={`No ${v.one('company')}`}
      nothingFoundMessage={`No ${v.one('company')} by that name`}
      data={data}
      value={value}
      onChange={(id) => onChange(id, data.find((d) => d.value === id)?.label ?? null)}
      {...rest}
    />
  );
}

/** "15 Oct 2026" from "2026-10-15". */
export function formatDay(raw: string | null | undefined): string {
  if (!raw) return '';
  const d = new Date(`${raw.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}
